import * as THREE from 'three';
import { FALLBACK_TEXTURE, NFT_TEXTURE_ABOVE_BLACKOUT } from '../constants';
import { TilesConfig } from '../context/ConfigurationContext';
import { Coordinate, FloorType, Tile } from '../types/floor';
import { RoomPlacingModel, RoomObjectType } from '../types/room';

export const getCoordinatesFromId = (
  id: number,
  width: number,
  height: number,
  useHeight?: boolean
) => {
  const xCoor = useHeight ? Math.floor(id / height) : id % width;
  const yCoor = useHeight ? id % height : Math.floor(id / width);
  return { x: xCoor, y: yCoor };
};

export const getTileByCoordinate = (
  x: number,
  y: number,
  floorMap: FloorType
): Tile | undefined => {
  const filtered = floorMap.tiles.find((r) => x === r.x && y === r.y);
  return filtered;
};

export const isCoordinateAnNFT = (
  x: number,
  y: number,
  floorMap: FloorType
): RoomObjectType | undefined => {
  const nftObject = floorMap.rooms.find(
    (c) =>
      c.startX > -1 &&
      c.endX > -1 &&
      c.startY > -1 &&
      c.endY > -1 &&
      x >= c.startX &&
      x <= c.endX &&
      y >= c.startY &&
      y <= c.endY
  );

  return nftObject;
};

export const getWorldPositionFromNFT = (
  nft: RoomObjectType,
  boxSize: number,
  amountOfBoxesX: number,
  amountOfBoxesY: number
) => {
  if (nft.endX > -1 && nft.startX > -1 && nft.endY > -1 && nft.startY > -1) {
    return {
      x: -nft.startY * boxSize - ((amountOfBoxesY - 1) * boxSize) / 2,
      y: NFT_TEXTURE_ABOVE_BLACKOUT,
      z: nft.startX * boxSize + ((amountOfBoxesX - 1) * boxSize) / 2,
    };
  } else {
    return {
      x: Number.MAX_SAFE_INTEGER,
      y: Number.MAX_SAFE_INTEGER,
      z: Number.MAX_SAFE_INTEGER,
    };
  }
};

export const getWorldPositionFromTile = (
  cell: Coordinate,
  boxSize: number,
  amountOfBoxesX: number,
  amountOfBoxesY: number
) => {
  return {
    x: -cell.startY * boxSize - ((amountOfBoxesY - 1) * boxSize) / 2,
    y: NFT_TEXTURE_ABOVE_BLACKOUT,
    z: cell.startX * boxSize + ((amountOfBoxesX - 1) * boxSize) / 2,
  };
};

export const randomFallbackImage = (seed: string) => {
  let id = 0;

  for (let i = 0; i < seed.length; i++) {
    id += seed.charCodeAt(i);
  }

  return FALLBACK_TEXTURE[Math.floor(id % FALLBACK_TEXTURE.length)];
};

export const loadTexturePromise = (src: string) => {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      src,
      (texture: THREE.Texture) => {
        resolve(texture);
      },
      () => {},
      async (err) => {
        const errMessage = err instanceof Error ? err.message : String(err);
        console.error(errMessage);
        console.warn('Using fallback texture');
        return new THREE.TextureLoader().load(
          randomFallbackImage(src),
          (texture: THREE.Texture) => {
            resolve(texture);
          }
        );
      }
    );
  });
};

export const isNFTAcceptedByTile = (
  tilesConfig: TilesConfig,
  tile: Tile,
  nft: RoomPlacingModel
) => {
  return tilesConfig[tile.type].allowed.includes(nft.type);
};

export const getFloorWidthAndHeight = (floor: FloorType) => {
  let width = 0;
  let height = 0;

  floor.tiles.forEach((tile) => {
    if (tile.x > width) {
      width = tile.x;
    }
    if (tile.y > height) {
      height = tile.y;
    }
  });
  width++;
  height++;

  return { width, height };
};

export const getRandomColor = () =>
  '#' +
  [...Array(6)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');

export const getBoundingBox = (coords: Coordinate[]) => {
  return {
    startX: coords.reduce((p, c) => Math.min(p, c.startX), Infinity),
    startY: coords.reduce((p, c) => Math.min(p, c.startY), Infinity),
    endX: coords.reduce((p, c) => Math.max(p, c.startX), 0),
    endY: coords.reduce((p, c) => Math.max(p, c.startY), 0),
  };
};
